type PlanRestriction = {
    slug: string;
    name: string;
    description: string;
    type: "boolean" | "number";
};

export const QuestionTypes = Object.freeze({
    post: {
        slug: "post",
        name: "Post",
        description: "No responses, only comments",
        color: "lime-600",
        icon: "material-symbols:post-outline-rounded",
    },
    text: {
        slug: "text",
        name: "Text",
        description: "A free text response",
        color: "indigo-600",
        icon: "mdi:text-box",
    },
    word: {
        slug: "word",
        name: "Word",
        description: "A single word response",
        color: "sky-600",
        icon: "fluent:text-t-12-regular",
    },
    multiple_choice: {
        slug: "multiple_choice",
        name: "Multiple Choice",
        description: "Select from a group of options",
        color: "orange-600",
        icon: "fluent:select-all-on-16-filled",
    },
    ranking: {
        slug: "ranking",
        name: "Ranking",
        description: "Drag and drop options to rank them in order",
        color: "emerald-600",
        icon: "tdesign:order-descending",
    },
    slider: {
        slug: "slider",
        name: "Slider",
        description: "Slide a marker to select a value",
        color: "green-600",
        icon: "fluent-mdl2:slider",
    },
    weighting: {
        slug: "weighting",
        name: "Weighting",
        description: "Distribute a number of points across options",
        color: "amber-600",
        icon: "material-symbols:line-weight-rounded",
    },
    voice: {
        slug: "voice",
        name: "Voice",
        description: "Record an audio response",
        color: "fuchsia-600",
        icon: "tabler:microphone-filled",
    },
    annotation: {
        slug: "annotation",
        name: "Annotation",
        description: "Free text responses linked to a document/image",
        color: "indigo-600",
        icon: "mdi:draw-pen",
    },
    gatekeeper: {
        slug: "gatekeeper",
        name: "Gatekeeper",
        description: "A gatekeeper question",
        color: "red-600",
        icon: "mdi:gate",
    },
});

export const QuestionParams = Object.freeze({
    slider_range: {
        name: "Allow range (low - high)",
        slug: "slider_range",
        editable: false,
    },
    randomize_options: {
        name: "Randomize options order",
        slug: "randomize_options",
        editable: true,
    },
    allow_annotations: {
        name: "Allow annotations",
        slug: "allow_annotations",
        editable: false,
    },
    allow_multiple: {
        name: "Allow multiple responses",
        slug: "allow_multiple",
        editable: false,
    },
    show_charts: {
        name: "Show results charts",
        slug: "show_charts",
        editable: true,
    },
    show_responses: {
        name: "Show others' responses",
        slug: "show_responses",
        editable: true,
    },
    allow_voting: {
        name: "Allow voting",
        slug: "allow_voting",
        editable: false,
    },
    allow_comments: {
        name: "Show comments panel",
        slug: "allow_comments",
        editable: true,
    },
    disable_unresponded_comments: {
        name: "Disable comments until question has been responded to",
        slug: "disable_unresponded_comments",
        editable: false,
    },
    response_limit: {
        name: "Response limit",
        slug: "response_limit",
        editable: false,
    },
    cover_image_type: {
        name: "Cover image type",
        slug: "cover_image_type",
        editable: false,
    },
});

export const StimulusTypes = Object.freeze({
    document: {
        slug: "document",
        name: "Document",
        icon: "basil:document-outline",
    },
    slides: {
        slug: "slides",
        name: "Slides",
        icon: "bi:images",
    },
    video: {
        slug: "video",
        name: "Video",
        icon: "ph:video-fill",
    },
    audio: {
        slug: "audio",
        name: "Audio",
        icon: "fluent:headphones-sound-wave-24-filled",
    },
    image: {
        slug: "image",
        name: "Image",
        icon: "fa6-solid:image",
    },
    url: {
        slug: "url",
        name: "URL",
        icon: "humbleicons:url",
    },
    question: {
        slug: "question",
        name: "Question",
        icon: "bi:question-square",
    },
});

export const Severity = Object.freeze({
    info: {
        class: "blue-600",
    },
    success: {
        class: "green-600",
    },
    warning: {
        class: "amber-600",
    },
    error: {
        class: "red-600",
    },
});

export const PlanRestrictions = Object.freeze({
    group_limit: {
        slug: "group_limit",
        name: "Groups",
        description: "Maximum number of groups allowed",
        type: "number",
    },
    group_member_limit: {
        slug: "group_member_limit",
        name: "Group Members",
        description: "Maximum number of members per group",
        type: "number",
    },
    post_limit: {
        slug: "post_limit",
        name: "Posts",
        description: "Maximum number of posts allowed",
        type: "number",
    },
    ai_tokens_limit: {
        slug: "ai_tokens_limit",
        name: "AI Tokens",
        description: "Maximum number of AI tokens allowed",
        type: "number",
    },
    can_ask_detailed_questions: {
        slug: "can_ask_detailed_questions",
        name: "Detailed Questions",
        description: "Ability to ask detailed questions",
        type: "boolean",
    },
    can_ask_annotation_questions: {
        slug: "can_ask_annotation_questions",
        name: "Annotation Questions",
        description: "Ability to ask annotation questions",
        type: "boolean",
    },
    can_reuse_questions: {
        slug: "can_reuse_questions",
        name: "Reuse Questions",
        description: "Ability to reuse existing questions and stimuli",
        type: "boolean",
    },
    can_export: {
        slug: "can_export",
        name: "Data Export",
        description: "Ability to export data and reports",
        type: "boolean",
    },
    ofType(type: "boolean" | "number"): PlanRestriction[] {
        return Object.values(this).filter(
            (r): r is PlanRestriction => typeof r === "object" && "type" in r && r.type === type,
        );
    },
});

export const GroupRole = Object.freeze({
    admin: {
        name: "Admin",
        value: 1,
    },
    creator: {
        name: "Creator",
        value: 2,
    },
    member: {
        name: "Member",
        value: 3,
    },
});

export const AiChatSources = Object.freeze({
    group: {
        name: "Group",
        value: 1,
    },
    question: {
        name: "Question",
        value: 2,
    },
    help: {
        name: "Help",
        value: 3,
    },
});

export const AiPromptCategoryOptions = Object.freeze({
    group_analysis: {label: "Group Analysis", value: 1},
    question_analysis: {label: "Question Analysis", value: 2},
    text_generation: {label: "Text Generation", value: 3},
    ask_albie: {label: "Ask Albie", value: 4},
});

type ColorOption = {
    name: string;
    class: string;
    hex: string;
};

type ColorMap = {
    [key: string]: ColorOption | (() => string);
    random(): string;
};

export const Colors = Object.freeze<ColorMap>({
    white: {name: "White", class: "white", hex: "#ffffff"},
    black: {name: "Black", class: "black", hex: "#000000"},
    slate: {name: "Slate", class: "slate-600", hex: "#475569"},
    gray: {name: "Gray", class: "gray-600", hex: "#4b5563"},
    zinc: {name: "Zinc", class: "zinc-600", hex: "#52525b"},
    neutral: {name: "Neutral", class: "neutral-600", hex: "#525252"},
    stone: {name: "Stone", class: "stone-600", hex: "#57534e"},
    red: {name: "Red", class: "red-600", hex: "#dc2626"},
    orange: {name: "Orange", class: "orange-600", hex: "#ea580c"},
    amber: {name: "Amber", class: "amber-600", hex: "#d97706"},
    yellow: {name: "Yellow", class: "yellow-600", hex: "#ca8a04"},
    lime: {name: "Lime", class: "lime-600", hex: "#65a30d"},
    green: {name: "Green", class: "green-600", hex: "#16a34a"},
    emerald: {name: "Emerald", class: "emerald-600", hex: "#059669"},
    teal: {name: "Teal", class: "teal-600", hex: "#0d9488"},
    cyan: {name: "Cyan", class: "cyan-600", hex: "#0891b2"},
    sky: {name: "Sky", class: "sky-600", hex: "#0284c7"},
    blue: {name: "Blue", class: "blue-600", hex: "#2563eb"},
    indigo: {name: "Indigo", class: "indigo-600", hex: "#4f46e5"},
    violet: {name: "Violet", class: "violet-600", hex: "#7c3aed"},
    purple: {name: "Purple", class: "purple-600", hex: "#9333ea"},
    fuchsia: {name: "Fuchsia", class: "fuchsia-600", hex: "#c026d3"},
    pink: {name: "Pink", class: "pink-600", hex: "#db2777"},
    rose: {name: "Rose", class: "rose-600", hex: "#e11d48"},
    random(): string {
        const keys = Object.keys(Colors).filter((k) => k !== "random");
        return keys[Math.floor(Math.random() * keys.length)];
    },
});
